import { makeAutoObservable } from "mobx";
import io from 'socket.io-client';
import CallStore from "./CallStore";
import ChatStore from "./ChatStore";
import MainStore from "./MainStore";

/**
 * Socket store for managing WebSocket connections and real-time communication
 * Handles customer and technician socket connections, drawing events, and chat messaging
 */
class SocketStore {

  /** @type {string} Base URL for WebSocket connections */
  baseUrl = 'https://demo-api.selffix.co';
  // baseUrl = 'https://v2selffixapi.monkeysteam.com';
  // baseUrl = 'http://192.168.0.173:3000';

  /** @type {Socket|null} Customer WebSocket connection */
  customerSocket = null;

  /** @type {boolean} Whether customer is connected */
  customerConnected = false;

  /** @type {Socket|null} Technician WebSocket connection */
  techSocket = null;

  /** @type {boolean} Whether technician is connected */
  techConnected = false;

  /** @type {Array} Array of drawn shapes for real-time synchronization */
  shapes = [];

  /** @type {string|null} Currently selected drawing shape */
  selectedShape = null;

  /** @type {string|null} Currently selected shape color */
  selectedShapeColor = null;

  /** @type {Object|null} Freehand drawing location data */
  freeHandShapeLocation = null;

  /**
   * Initialize the socket store
   * Sets up MobX observables for reactive state management
   */
  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Set the currently selected drawing shape
   * @param {string} shape - Shape type (free-hand, circle, line-up, etc.)
   * @returns {void}
   */
  setSelectedShape(shape) {
    this.selectedShape = shape;
  }

  /**
   * Set the color for drawing shapes
   * @param {string} color - Color value (hex, rgb, etc.)
   * @returns {void}
   */
  setSelectedShapeColor(color) {
    this.selectedShapeColor = color;
  }

  /**
   * Clear all shapes from the screen
   * Sends remove events for each shape and clears local array
   * @returns {void}
   */
  clearScreen() {
    this.shapes.forEach(shape => {
      this.sendShapeRemove(shape.id);
    });
    this.shapes = [];
  }

  /**
   * Set freehand drawing location data
   * @param {Object} location - Location coordinates for freehand drawing
   * @returns {void}
   */
  setFreeHandShapeLocation(location) {
    this.freeHandShapeLocation = location;
  }

  /**
   * Find shape at specific coordinates
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {Object|null} Shape object if found, null otherwise
   */
  getShapeByCoordinates(x, y) {
    return this.shapes.reverse().find(shape =>
      x >= shape.x - 24 && x <= shape.x + 24 &&
      y >= shape.y - 24 && y <= shape.y + 24
    );
  }

  // CUSTOMER

  /**
   * Send customer screen resolution to server
   * Emits screen dimensions for proper video scaling
   * @returns {void}
   */
  sendCustomerResolution = () => {
    this.customerSocket.emit("customer-screen-resolution", { width: window.innerWidth, height: window.innerHeight });
  }

  /**
   * Set customer connection state
   * @param {boolean} connected - Whether customer is connected
   * @returns {void}
   */
  setCustomerConnected(connected) {
    this.customerConnected = connected;
  }

  /**
   * Connect customer WebSocket to server
   * Establishes real-time connection with authentication
   * @param {string} slug - Call session identifier
   * @param {string} pin - 6-character PIN for customer access
   * @returns {void}
   */
  connectCustomerSocket(slug, pin) {
    if (this.customerConnected) return;
    this.customerSocket = io(this.baseUrl, {
      transports: ['websocket', 'polling'],
      secure: true,
      reconnection: true,
      autoConnect: true,
      auth: {
        slug: slug,
        pin: pin, // 6-character PIN
        userType: 'customer',
      },
    });

    this.customerSocket.on('connect', () => {
      this.setCustomerConnected(true);
    });


    this.customerSocket.on("disconnect", (reason) => {
    });

    this.customerSocket.on("ping", () => {
      this.customerSocket.emit("pong");
    });

    this.customerSocket.on("connect_error", (err) => {
      if (err.data?.code === 'CALL_ENDED') {
        window.location.href = '/customer/thanks';
        return;
      }
      console.error("🚫 Bağlantı hatası:", err.message);
      MainStore.showModalAlert({
        title: 'Connection error',
        message: err.data ? err.data.message : err.message,
        type: 'error',
        confirmText: 'GO TO HOME',
        onConfirm: () => {
          window.location.reload();
        },
        onCancel: null,
      });
    });

    this.customerSocket.on("call-token", (data) => {
      const storageToken = localStorage.getItem('callToken');
      if (storageToken) {
        CallStore.setCustomerCallToken(storageToken);
        return;
      }
      localStorage.setItem('callToken', data);
      CallStore.setCustomerCallToken(data);
    });


    this.customerSocket.on("call-state", (data) => {

      ChatStore.setMessages(data.call.messages.map(message => ({
        ...message,
        message: {
          type: message.message.type === 'file' ? 'file' : 'text',
          content: message.message.type === 'file' ? JSON.parse(message.message.content) : message.message.content
        }
      })));
      ChatStore.setCallState(data.call);
    });


    this.customerSocket.on("new-message", (data) => {
      ChatStore.addMessage(data);
      if (data.from === 'technician') {
        ChatStore.setUnreadMessages(1);
      }

      // {
      //   from: 'customer' | 'technician',
      //   message: {
      //     type: 'text' | 'file',
      //     content: 'message content'
      //   },
      //   timestamp: Date,
      //   ip: 'sender-ip'
      // }
    });


    this.customerSocket.on("user-typing", (data) => {
      ChatStore.setIsTyping(data.isTyping);
    });

    this.customerSocket.on("request-customer-screen-resolution", (data) => {
      this.sendCustomerResolution();
    });


    this.customerSocket.on("shape-request", (data) => {
      this.shapes.push({
        ...data,
        color: data.color || '#000000',
      });
      // {
      //   x: number,
      //   y: number,
      //   shape: 'circle' | 'rectangle' | 'line',
      //   color: string,
      //   id: string,
      // }
    });


    this.customerSocket.on("free-hand-shape", (data) => {
      this.setFreeHandShapeLocation(data);
    });


    this.customerSocket.on("shape-remove", (data) => {
      this.shapes = this.shapes.filter(shape => shape.id !== data.id);
    });


    this.customerSocket.on("call-ended", (data) => {
      CallStore.setIsOpenEndCallByTechForCustomer(true);
      // {
      //   reason: 'customer-end-call',
      // }
    });


    this.customerSocket.on("toggle-torch", (data) => {
      if (data.from === 'technician') {
        CallStore.setIsTorchOn(data.isOn);
      }
    });
  }

  /**
   * Send message from customer to technician
   * @param {Object} message - Message object with content and metadata
   * @returns {void}
   */
  sendCustomerMessage(message) {
    this.customerSocket.emit("send-message", message);
  }

  /**
   * Send typing indicator from customer
   * @param {boolean} isTyping - Whether customer is typing
   * @returns {void}
   */
  sendCustomerTyping(isTyping) {
    this.customerSocket.emit("typing", { isTyping });
  }

  /**
   * Send end call request from customer
   * @returns {void}
   */
  sendCustomerEndCall() {
    this.customerSocket.emit("end-call-by-customer", {
      reason: 'Customer End Call',
    });
  }

  /**
   * Send torch toggle request from customer
   * @param {boolean} isOn - Whether torch should be on
   * @returns {void}
   */
  sendCustomerTorch(isOn) {
    this.customerSocket.emit("toggle-torch", {
      from: 'customer',
      isOn: isOn,
    });
  }

  // TECHNICIAN

  /**
   * Set technician connection state
   * @param {boolean} connected - Whether technician is connected
   * @returns {void}
   */
  setTechConnected(connected) {
    this.techConnected = connected;
  }

  /**
   * Connect technician WebSocket to server
   * Establishes real-time connection with authentication
   * @param {string} slug - Call session identifier
   * @param {string} token - JWT authentication token
   * @returns {void}
   */
  connectTechSocket(slug, token) {
    if (this.techConnected) return;
    this.techSocket = io(this.baseUrl, {
      transports: ['websocket', 'polling'],
      secure: true,
      reconnection: true,
      autoConnect: true,
      auth: {
        slug: slug,
        token: token, // 6-character PIN
        userType: 'technician',
      },
    });

    this.techSocket.on("disconnect", (reason) => {
    });

    this.techSocket.on("ping", () => {
      this.techSocket.emit("pong");
    });

    this.techSocket.on("connect_error", (err) => {
      console.error("🚫 Bağlantı hatası:", err.message);
      MainStore.showModalAlert({
        title: 'Connection error',
        message: err.data ? err.data.message : err.message,
        type: 'error',
        confirmText: 'GO TO HOME',
        onConfirm: () => {
          window.location.href = '/tech/dashboard';
        },
        onCancel: null,
      });
    });


    this.techSocket.on("call-state", (data) => {

      ChatStore.setMessages(data.call.messages.map(message => ({
        ...message,
        message: {
          type: message.message.type === 'file' ? 'file' : 'text',
          content: message.message.type === 'file' ? JSON.parse(message.message.content) : message.message.content
        }
      })));
      ChatStore.setCallState(data.call);
    });

    this.techSocket.on("participant-joined", (data) => {
    });


    this.techSocket.on("participant-left", (data) => {

    });


    this.techSocket.on("new-message", (data) => {
      ChatStore.addMessage(data);
    });

    this.techSocket.on("user-typing", (data) => {
      ChatStore.setIsTyping(data.isTyping);
    });


    this.techSocket.on("customer-screen-resolution", (data) => {
      CallStore.setCustomerScreenResolution(data);
    });

    this.techSocket.on("end-call-by-customer", (data) => {
      CallStore.setIsOpenEndCallByCustomerForTech(true);
    });

    this.techSocket.on("toggle-torch", (data) => {
      if (data.from === 'customer') {
        CallStore.setIsTorchOn(data.isOn);
      }
    });


  }

  /**
   * Send message from technician to customer
   * @param {Object} message - Message object with content and metadata
   * @returns {void}
   */
  sendTechMessage(message) {
    this.techSocket.emit("send-message", message);
  }

  /**
   * Send typing indicator from technician
   * @param {boolean} isTyping - Whether technician is typing
   * @returns {void}
   */
  sendTechTyping(isTyping) {
    this.techSocket.emit("typing", { isTyping });
  }

  /**
   * Send shape click event for drawing tools
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {string} shape - Shape type
   * @param {string} color - Shape color
   * @returns {void}
   */
  sendShapeClick(x, y, shape, color) {
    const id = Math.random().toString(36).substring(2, 15);
    this.techSocket.emit("shape-request", { x, y, shape, id, color });
    this.shapes.push({ x, y, shape, id, color });
  }

  /**
   * Send freehand drawing coordinates
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {void}
   */
  sendFreeHandShape(x, y) {
    this.techSocket.emit("free-hand-shape", { x, y, color: this.selectedShapeColor });
    this.setFreeHandShapeLocation({ x, y });
  }

  /**
   * Send shape removal request
   * @param {string} id - Shape ID to remove
   * @returns {void}
   */
  sendShapeRemove(id) {
    this.techSocket.emit("shape-remove", { id });
    this.shapes = this.shapes.filter(shape => shape.id !== id);
  }

  /**
   * Send torch toggle request from technician
   * @param {boolean} isOn - Whether torch should be on
   * @returns {void}
   */
  sendTorch(isOn) {
    this.techSocket.emit("toggle-torch", {
      from: 'technician',
      isOn: isOn,
    });
  }

}

export default new SocketStore();